// ApiComposer.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { cleanString, resolveBaseUrl } from '../apiUtils';
import { getFieldTypeIconKey } from 'utilities/FieldTypeIcons';
import useApiComposer from './useApiComposer';

// Services
import { runApiTestRequest } from 'services/api/ApiAccessService';

// CSS
import './ApiComposer.css';

// Components
import Button from 'components/button/Button';
import RichSelect from 'components/richselect/RichSelect';
import TextInput from 'components/textinput/TextInput';
import Divider from 'components/divider/Divider';

// Utilities
import {
  COMPOSER_RELATIVE_CALENDAR_UNITS,
  COMPOSER_RELATIVE_DATE_UNITS,
  isComposerEnumMultiOperator,
  getComposerOperatorOptions,
  isComposerExistsOperator,
  isComposerTemporalFieldType,
  isComposerTemporalOperatorNeedsUnit,
  isComposerTemporalOperatorNeedsValue,
  isComposerTemporalValuelessOperator,
  isComposerValuelessOperator,
  normalizeComposerCalendarUnit,
  normalizeComposerRelativeUnit,
  resolveComposerDefaultOperator,
  resolveComposerFieldType,
} from './apiComposerFilters';

// Icons
import Icon from 'svg/Icon';

/**
 * ApiComposer
 *
 * Renders the Workspace API request composer form, generated request snippets, and read-only test results.
 *
 * @param {string} workspaceId - Active workspace id.
 * @param {boolean} nodeTypeCacheReady - Whether node types cache has finished loading.
 * @param {array} schemaTypes - Available node types for selector options.
 *
 * @returns {ReactNode} The rendered component.
 */
const ApiComposer = ({
  workspaceId,
  nodeTypeCacheReady,
  schemaTypes,
}) => {

  // ----- COMPOSER HOOK ----- //

  const {
    composerIntent,
    composerTypeId,
    composerNodeId,
    composerLimit,
    composerFieldId,
    composerOperator,
    composerPredicateValue,
    composerPredicateJoin,
    composerExtraQueryFilters,
    composerQuerySorts,
    composerWriteTitle,
    composerWriteValues,
    composerBusy,
    composerError,
    composerResult,
    selectedField,
    selectedFields,
    composerRequest,
    composerPreview,
    composerCurlSnippet,
    composerFetchSnippet,
    setComposerIntent: onComposerIntentChange,
    setComposerTypeId: onComposerTypeIdChange,
    setComposerNodeId: onComposerNodeIdChange,
    setComposerLimit: onComposerLimitChange,
    setComposerFieldId: onComposerFieldIdChange,
    setComposerOperator: onComposerOperatorChange,
    setComposerPredicateValue: onComposerPredicateValueChange,
    setComposerPredicateJoin: onComposerPredicateJoinChange,
    setComposerExtraQueryFilters: onComposerExtraQueryFiltersChange,
    setComposerQuerySorts: onComposerQuerySortsChange,
    setComposerWriteTitle: onComposerWriteTitleChange,
    setComposerWriteValues: onComposerWriteValuesChange,
    handleCopy: onCopy,
    handleComposerTestRun: onComposerTestRun,
  } = useApiComposer({
    workspaceId,
    schemaTypes,
    baseUrl: resolveBaseUrl(),
    runTestRequest: runApiTestRequest,
  });

  // ----- CONTEXT ----- //

  // Theme Context
  const { theme } = useTheme();

  // ----- UTILITIES ----- //

  // Intent options that require a type selector.
  const showTypeSelector = ['list_fields', 'list_nodes', 'query_nodes', 'create_node'].includes(composerIntent);

  // Intent options that require a node id.
  const showNodeId = ['get_node', 'update_node', 'delete_node'].includes(composerIntent);

  // Whether query predicate fields should render.
  const showQueryFields = composerIntent === 'query_nodes';

  // Intent options that require write payload inputs.
  const showWriteFields = ['create_node', 'update_node'].includes(composerIntent);

  // Composer intent options for the intent selector dropdown in the form.
  const composerIntentOptions = [
    {
      value: 'list_types',
      label: 'List Types',
      iconKey: 'deployed_code',
      note: 'List all node types in this workspace.',
    },
    {
      value: 'list_fields',
      label: 'List Properties By Type',
      iconKey: 'schema',
      note: 'Return schema properties for one type.',
    },
    {
      value: 'list_nodes',
      label: 'List Nodes',
      iconKey: 'list_alt',
      note: 'Fetch recent nodes for the selected type.',
    },
    {
      value: 'query_nodes',
      label: 'Query Nodes',
      iconKey: 'filter_alt',
      note: 'Filter and sort nodes with predicates.',
    },
    {
      value: 'get_node',
      label: 'Get Node By ID',
      iconKey: 'search',
      note: 'Read one node using its node id.',
    },
    {
      value: 'create_node',
      label: 'Create Node',
      iconKey: 'add_circle',
      note: 'Generate payload to create a new node.',
    },
    {
      value: 'update_node',
      label: 'Update Node',
      iconKey: 'edit',
      note: 'Generate payload to patch an existing node.',
    },
    {
      value: 'delete_node',
      label: 'Delete Node',
      iconKey: 'delete',
      note: 'Generate request to delete a node by id.',
    },
  ];

  // Options for the type selector dropdown in the form, mapped from available schema types.
  const composerTypeOptions = (Array.isArray(schemaTypes) ? schemaTypes : []).map((row) => ({
    value: cleanString(row?.typeId),
    label: cleanString(row?.label) || cleanString(row?.typeId),
    iconKey: cleanString(row?.iconKey || row?.icon || row?.iconName) || 'deployed_code',
  }));

  // Options for boolean value selectors in the form, used for operators that require true/false inputs.
  const booleanValueOptions = [
    { value: 'true', label: 'True' },
    { value: 'false', label: 'False' },
  ];

  // Options for joining multiple query predicates in the form, allowing users to specify AND/OR 
  // logic between filters.
  const joinOptions = [
    { value: 'and', label: 'and' },
    { value: 'or', label: 'or' },
  ];

  // Options for sort direction selectors in the form, allowing users to specify ascending/descending
  const sortDirectionOptions = [
    { value: 'asc', label: 'Ascending' },
    { value: 'desc', label: 'Descending' },
  ];

  // Map of field definitions by field id for quick lookup when rendering query predicate rows.
  const fieldById = new Map(
    (Array.isArray(selectedFields) ? selectedFields : []).map((row) => [row.fieldId, row]),
  );

  // Utility function to resolve the default operator for a given field definition, used when adding new 
  // query predicate rows.
  const resolveDefaultOperatorForField = (fieldDef) => {
    const fieldType = resolveComposerFieldType(fieldDef, 'string');
    return resolveComposerDefaultOperator(fieldType);
  };

  // Utility function to normalize a predicate value based on the operator and field type, ensuring that the
  // value is in the correct format for the API request payload.
  const normalizeValueForOperator = (operator, currentValue, fieldType) => {
    const isEnumLikeField = fieldType === 'enum' || fieldType === 'multiselect' || fieldType === 'tags';
    if (isEnumLikeField) {
      if (isComposerEnumMultiOperator(operator)) {
        if (Array.isArray(currentValue)) {
          return currentValue.map((value) => cleanString(value)).filter(Boolean);
        }
        const single = cleanString(currentValue);
        return single ? [single] : [];
      }

      if (Array.isArray(currentValue)) {
        return cleanString(currentValue[0]);
      }
      return cleanString(currentValue);
    }

    if (isComposerTemporalFieldType(fieldType)) {
      const source = currentValue && typeof currentValue === 'object' && !Array.isArray(currentValue)
        ? currentValue
        : { value: currentValue, unit: null };

      if (isComposerTemporalOperatorNeedsValue(operator)) {
        const value = cleanString(source?.value);
        const unit = normalizeComposerRelativeUnit(source?.unit) || 'days';
        return {
          value: value || '1',
          unit,
        };
      }

      if (operator === 'is_this') {
        const unit = normalizeComposerCalendarUnit(source?.unit) || 'week';
        return {
          value: null,
          unit,
        };
      }

      if (isComposerTemporalValuelessOperator(operator)) {
        return {
          value: null,
          unit: null,
        };
      }
    }

    if (isComposerExistsOperator(operator)) {
      return cleanString(currentValue).toLowerCase() === 'false' ? 'false' : 'true';
    }
    if (isComposerValuelessOperator(operator)) {
      return '';
    }
    if (currentValue && typeof currentValue === 'object' && !Array.isArray(currentValue)) {
      return '';
    }
    return currentValue ?? '';
  };

  // Utility function to map the API schema response for download, transforming field definitions to 
  // use 'key' instead of 'fieldId' when they are the same, and recursively mapping nested objects and arrays.
  const composerFieldOptions = (Array.isArray(selectedFields) ? selectedFields : []).map((row) => ({
    value: cleanString(row?.fieldId),
    label: cleanString(row?.label || row?.fieldId),
    iconKey: getFieldTypeIconKey(row?.type, 'deployed_code'),
    note: cleanString(row?.type),
  }));

  // Recursive function to map the API schema response for download, transforming field definitions to use 
  // 'key' instead of 'fieldId' when they are the same, and recursively mapping nested objects and arrays.
  const buildPredicateUiMeta = (fieldDef, predicateValue, operator) => {
    const fieldType = cleanString(fieldDef?.type).toLowerCase();
    const fieldOptions = Array.isArray(fieldDef?.options)
      ? fieldDef.options
        .map((optionRow) => {
          const value = cleanString(optionRow?.value || optionRow);
          const label = cleanString(optionRow?.label || optionRow?.value || optionRow) || value;
          return {
            value,
            label,
          };
        })
        .filter((optionRow) => optionRow.value)
      : [];

    const isBooleanField = ['boolean', 'bool', 'checkbox'].includes(fieldType);
    const selectOptions = isBooleanField
      ? [
        { value: 'true', label: 'True' },
        { value: 'false', label: 'False' },
      ]
      : fieldOptions;

    const useSelect = isBooleanField
      || ((fieldType === 'enum' || fieldType === 'multiselect' || fieldType === 'tags') && fieldOptions.length > 0);
    const useCheckboxList = (fieldType === 'enum' || fieldType === 'multiselect' || fieldType === 'tags')
      && fieldOptions.length > 0
      && isComposerEnumMultiOperator(operator);

    const normalizedValue = cleanString(predicateValue);
    const selectValue = useSelect
      && selectOptions.some((optionRow) => optionRow.value === normalizedValue)
      ? normalizedValue
      : '';
    const checkboxValues = Array.isArray(predicateValue)
      ? predicateValue.map((value) => cleanString(value)).filter(Boolean)
      : (normalizedValue ? [normalizedValue] : []);

    return {
      fieldType,
      isBooleanField,
      useSelect,
      useCheckboxList,
      selectOptions,
      selectValue,
      checkboxValues,
    };
  };

  // Options for joining multiple query predicates in the form, allowing users to specify AND/OR logic between filters.
  const queryFilterRows = [
    {
      key: 'composer-filter-primary',
      kind: 'primary',
      fieldId: composerFieldId,
      operator: composerOperator,
      value: composerPredicateValue,
      joinToNext: composerPredicateJoin,
    },
    ...(Array.isArray(composerExtraQueryFilters)
      ? composerExtraQueryFilters.map((row, index) => ({
        ...row,
        key: `composer-filter-extra-${index}`,
        kind: 'extra',
        index,
      }))
      : []),
  ];

  // ----- HANDLERS ----- //

  /**
   * Handles adding a new query predicate row in the form, using the currently selected field to determine the 
   * default operator
   */
  const handleAddComposerFilter = () => {
    if (selectedFields.length === 0) return;
    const firstField = selectedFields[0] || null;
    const firstFieldType = resolveComposerFieldType(firstField, 'string');
    const defaultOperator = resolveDefaultOperatorForField(firstField);
    onComposerExtraQueryFiltersChange((prev) => ([
      ...prev,
      {
        fieldId: firstField?.fieldId || '',
        operator: defaultOperator,
        value: normalizeValueForOperator(defaultOperator, '', firstFieldType),
        joinToNext: 'and',
      },
    ]));
  };

  /**
   * Handles removing a query predicate row from the form, ensuring that at least one row remains and updating the
   * primary filter values if the primary row is removed. 
   * 
   * Uses the 'index' field on extra filters to identify which
   * row to remove from the extra filters array in state, and resets to the first extra filter if the primary filter 
   * is removed.
   * 
   * @param {object} filterRow - The filter row object to remove, containing properties to identify the row and whether it is primary or extra.
   */
  const handleRemoveComposerFilter = (filterRow) => {
    if (queryFilterRows.length <= 1) return;

    if (filterRow?.kind === 'primary') {
      if (!Array.isArray(composerExtraQueryFilters) || composerExtraQueryFilters.length === 0) return;
      const [nextPrimaryFilter, ...remainingFilters] = composerExtraQueryFilters;
      onComposerFieldIdChange(cleanString(nextPrimaryFilter?.fieldId));
      onComposerOperatorChange(cleanString(nextPrimaryFilter?.operator || 'eq') || 'eq');
      onComposerPredicateValueChange(nextPrimaryFilter?.value || '');
      onComposerPredicateJoinChange(cleanString(nextPrimaryFilter?.joinToNext).toLowerCase() === 'or' ? 'or' : 'and');
      onComposerExtraQueryFiltersChange(remainingFilters);
      return;
    }

    const extraFilterIndex = Number.isInteger(filterRow?.index) ? filterRow.index : -1;
    if (extraFilterIndex < 0) return;
    onComposerExtraQueryFiltersChange((prev) => prev.filter((_, rowIndex) => rowIndex !== extraFilterIndex));
  };

  // ----- RENDER ----- //

  return (

    <section className="api-composer-container">

      {/* COMPOSER FORM */}
      <div className="api-composer-form"
        style={{
          width: '500px',
        }}>

        {/* INTENT */}
        <div className="api-composer-field">
          <label
            className="api-composer-label"
            style={{
              color: theme.foregroundMuted,
            }}
            htmlFor="composer-intent"
          >
            Intent
          </label>
          <RichSelect
            id="composer-intent"
            value={composerIntent}
            options={composerIntentOptions}
            placeholder="Select an intent"
            onChange={(event) => onComposerIntentChange(cleanString(event.target.value))}
          />
        </div>

        {/* LIMIT */}
        {(composerIntent === 'list_nodes' || composerIntent === 'query_nodes') && (
          <div className="api-composer-field">
            <label
              className="api-composer-label"
              style={{
                color: theme.foregroundMuted,
              }}
              htmlFor="composer-limit"
            >
              Limit
            </label>
            <TextInput
              type="number"
              id="composer-limit"
              value={composerLimit}
              placeholder="25"
              width={100}
              onChange={(event) => onComposerLimitChange(event.target.value)}
            />
          </div>
        )}

        {showTypeSelector && (
          <div className="api-composer-field">
            <label
              className="api-composer-label"
              style={{
                color: theme.foregroundMuted,
              }}
              htmlFor="composer-type"
            >
              Type
            </label>
            <RichSelect
              id="composer-type"
              value={composerTypeId}
              options={composerTypeOptions}
              emptyText={nodeTypeCacheReady ? 'No types loaded' : 'Loading node types cache...'}
              placeholder={nodeTypeCacheReady ? 'Select a type' : 'Loading node types cache...'}
              onChange={(event) => onComposerTypeIdChange(cleanString(event.target.value))}
            />
          </div>
        )}

        {showNodeId && (
          <div className="api-composer-field">
            <label
              className="api-composer-label"
              style={{
                color: theme.foregroundMuted,
              }}
              htmlFor="composer-node-id"
            >
              Node ID
            </label>
            <TextInput
              id="composer-node-id"
              value={composerNodeId}
              placeholder="node_123"
              onChange={(event) => onComposerNodeIdChange(event.target.value)}
            />
          </div>
        )}

        {showQueryFields && (
          <>
            <div className="api-composer-field api-composer-field-span-2 api-composer-advanced-section">
              <div className="api-composer-advanced-header">
                <label
                  className="api-composer-label"
                  style={{
                    color: theme.foregroundMuted,
                  }}
                >
                  Filters
                </label>
                <Button
                  label="+ Add Filter"
                  fontSize="11px"
                  height="28px"
                  padding="0 10px"
                  foregroundColor={theme.foreground}
                  style={{
                    borderColor: theme.status,
                    color: theme.foreground,
                  }}
                  disabled={selectedFields.length === 0}
                  onClick={handleAddComposerFilter}
                />
              </div>

              {queryFilterRows.map((filterRow, filterRowIndex) => {
                const isPrimaryFilter = filterRow?.kind === 'primary';
                const extraFilterIndex = Number.isInteger(filterRow?.index) ? filterRow.index : -1;
                const fieldDef = fieldById.get(cleanString(filterRow?.fieldId)) || (isPrimaryFilter ? selectedField : null);
                const fieldType = resolveComposerFieldType(fieldDef, 'string');
                const rowOperatorOptions = getComposerOperatorOptions(fieldType);
                const defaultOperator = rowOperatorOptions[0]?.value || 'eq';
                const rawFilterOperator = cleanString(filterRow?.operator || defaultOperator).toLowerCase() || defaultOperator;
                const filterOperator = rowOperatorOptions.some((optionRow) => optionRow.value === rawFilterOperator)
                  ? rawFilterOperator
                  : defaultOperator;
                const filterUi = buildPredicateUiMeta(fieldDef, filterRow?.value, filterOperator);
                const selectRichOptions = filterUi.selectOptions.map((optionRow) => ({
                  value: optionRow.value,
                  label: filterUi.isBooleanField
                    ? optionRow.label
                    : optionRow.label === optionRow.value
                      ? optionRow.label
                      : `${optionRow.label} (${optionRow.value})`,
                }));
                const hasNextRow = filterRowIndex < queryFilterRows.length - 1;
                const isTemporalField = isComposerTemporalFieldType(fieldType);
                const temporalNeedsValue = isTemporalField && isComposerTemporalOperatorNeedsValue(filterOperator);
                const temporalNeedsUnit = isTemporalField && isComposerTemporalOperatorNeedsUnit(filterOperator);
                const temporalValue = isTemporalField
                  ? normalizeValueForOperator(filterOperator, filterRow?.value, fieldType)
                  : null;
                const isExistsFilter = isComposerExistsOperator(filterOperator);
                const showValueInput = isTemporalField
                  ? (temporalNeedsValue || temporalNeedsUnit)
                  : !isComposerValuelessOperator(filterOperator);
                const canRemove = queryFilterRows.length > 1;

                return (
                  <div
                    key={filterRow.key}
                    className="api-composer-query-row"
                  >
                    <RichSelect
                      className="api-composer-rich-input"
                      style={{
                        backgroundColor: theme.input,
                        borderColor: theme.backgroundHover,
                        color: theme.foreground,
                      }}
                      options={composerFieldOptions}
                      emptyText="No fields loaded"
                      placeholder={selectedFields.length > 0 ? 'Select a field' : 'No fields loaded'}
                      disabled={selectedFields.length === 0}
                      value={cleanString(filterRow?.fieldId)}
                      onChange={(event) => {
                        const nextFieldId = cleanString(event.target.value);
                        const nextFieldDef = fieldById.get(nextFieldId) || null;
                        const nextFieldType = resolveComposerFieldType(nextFieldDef, 'string');
                        const nextOperator = resolveDefaultOperatorForField(nextFieldDef);
                        if (isPrimaryFilter) {
                          onComposerFieldIdChange(nextFieldId);
                          onComposerOperatorChange(nextOperator);
                          onComposerPredicateValueChange(normalizeValueForOperator(nextOperator, composerPredicateValue, nextFieldType));
                          return;
                        }
                        if (extraFilterIndex < 0) return;
                        onComposerExtraQueryFiltersChange((prev) => prev.map((row, rowIndex) => {
                          if (rowIndex !== extraFilterIndex) return row;
                          return {
                            ...row,
                            fieldId: nextFieldId,
                            operator: nextOperator,
                            value: normalizeValueForOperator(nextOperator, row?.value, nextFieldType),
                          };
                        }));
                      }}
                    />

                    <RichSelect
                      options={rowOperatorOptions}
                      value={filterOperator}
                      placeholder="Select operator"
                      onChange={(event) => {
                        const nextOperator = cleanString(event.target.value);
                        if (isPrimaryFilter) {
                          onComposerOperatorChange(nextOperator);
                          onComposerPredicateValueChange(normalizeValueForOperator(nextOperator, composerPredicateValue, fieldType));
                          return;
                        }
                        if (extraFilterIndex < 0) return;
                        onComposerExtraQueryFiltersChange((prev) => prev.map((row, rowIndex) => (
                          rowIndex === extraFilterIndex
                            ? {
                              ...row,
                              operator: nextOperator,
                              value: normalizeValueForOperator(nextOperator, row?.value, fieldType),
                            }
                            : row
                        )));
                      }}
                    />

                    {showValueInput ? (
                      isTemporalField ? (
                        <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                          {temporalNeedsValue && (
                            <TextInput
                              type="number"
                              value={cleanString(temporalValue?.value || '1')}
                              placeholder="1"
                              onChange={(event) => {
                                const nextValue = event.target.value;
                                const nextTemporalValue = {
                                  value: nextValue,
                                  unit: temporalValue?.unit || 'days',
                                };

                                if (isPrimaryFilter) {
                                  onComposerPredicateValueChange(nextTemporalValue);
                                  return;
                                }

                                if (extraFilterIndex < 0) return;
                                onComposerExtraQueryFiltersChange((prev) => prev.map((row, rowIndex) => (
                                  rowIndex === extraFilterIndex ? { ...row, value: nextTemporalValue } : row
                                )));
                              }}
                            />
                          )}

                          {temporalNeedsUnit && (
                            <RichSelect
                              options={filterOperator === 'is_this' ? COMPOSER_RELATIVE_CALENDAR_UNITS : COMPOSER_RELATIVE_DATE_UNITS}
                              value={cleanString(temporalValue?.unit || (filterOperator === 'is_this' ? 'week' : 'days'))}
                              placeholder="Select unit"
                              onChange={(event) => {
                                const nextUnit = cleanString(event.target.value);
                                const nextTemporalValue = {
                                  value: temporalNeedsValue ? cleanString(temporalValue?.value || '1') : null,
                                  unit: nextUnit,
                                };

                                if (isPrimaryFilter) {
                                  onComposerPredicateValueChange(nextTemporalValue);
                                  return;
                                }

                                if (extraFilterIndex < 0) return;
                                onComposerExtraQueryFiltersChange((prev) => prev.map((row, rowIndex) => (
                                  rowIndex === extraFilterIndex ? { ...row, value: nextTemporalValue } : row
                                )));
                              }}
                            />
                          )}
                        </div>
                      ) : isExistsFilter ? (
                        <RichSelect
                          options={booleanValueOptions}
                          value={cleanString(filterRow?.value).toLowerCase() === 'false' ? 'false' : 'true'}
                          placeholder="Select value"
                          onChange={(event) => {
                            const nextValue = cleanString(event.target.value);
                            if (isPrimaryFilter) {
                              onComposerPredicateValueChange(nextValue);
                              return;
                            }
                            if (extraFilterIndex < 0) return;
                            onComposerExtraQueryFiltersChange((prev) => prev.map((row, rowIndex) => (
                              rowIndex === extraFilterIndex ? { ...row, value: nextValue } : row
                            )));
                          }}
                        />
                      ) : filterUi.useCheckboxList ? (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '6px',
                            maxHeight: '120px',
                            overflowY: 'auto',
                            padding: '8px',
                            border: `1px solid ${theme.backgroundHover}`,
                            borderRadius: '8px',
                            backgroundColor: theme.input,
                          }}
                        >
                          {filterUi.selectOptions.map((optionRow) => {
                            const checked = filterUi.checkboxValues.includes(optionRow.value);

                            return (
                              <label
                                key={optionRow.value}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  color: theme.foreground,
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(event) => {
                                    const nextChecked = event.target.checked;
                                    const nextValues = nextChecked
                                      ? [...new Set([...filterUi.checkboxValues, optionRow.value])]
                                      : filterUi.checkboxValues.filter((value) => value !== optionRow.value);

                                    if (isPrimaryFilter) {
                                      onComposerPredicateValueChange(nextValues);
                                      return;
                                    }

                                    if (extraFilterIndex < 0) return;
                                    onComposerExtraQueryFiltersChange((prev) => prev.map((row, rowIndex) => (
                                      rowIndex === extraFilterIndex ? { ...row, value: nextValues } : row
                                    )));
                                  }}
                                />
                                <span>
                                  {optionRow.label === optionRow.value
                                    ? optionRow.label
                                    : `${optionRow.label} (${optionRow.value})`}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      ) : filterUi.useSelect ? (
                        <RichSelect
                          options={selectRichOptions}
                          value={filterUi.selectValue}
                          placeholder="Select an option"
                          onChange={(event) => {
                            const nextValue = cleanString(event.target.value);
                            if (isPrimaryFilter) {
                              onComposerPredicateValueChange(nextValue);
                              return;
                            }
                            if (extraFilterIndex < 0) return;
                            onComposerExtraQueryFiltersChange((prev) => prev.map((row, rowIndex) => (
                              rowIndex === extraFilterIndex ? { ...row, value: nextValue } : row
                            )));
                          }}
                        />
                      ) : (
                        <TextInput
                          value={isPrimaryFilter ? composerPredicateValue : filterRow?.value || ''}
                          placeholder={isPrimaryFilter
                            ? (filterUi.fieldType ? `Value (${filterUi.fieldType})` : 'Value')
                            : filterOperator === 'between'
                              ? 'min,max'
                              : filterOperator === 'in'
                                ? 'value1,value2'
                                : 'Value'}
                          onChange={(event) => {
                            const nextValue = event.target.value;
                            if (isPrimaryFilter) {
                              onComposerPredicateValueChange(nextValue);
                              return;
                            }
                            if (extraFilterIndex < 0) return;
                            onComposerExtraQueryFiltersChange((prev) => prev.map((row, rowIndex) => (
                              rowIndex === extraFilterIndex ? { ...row, value: nextValue } : row
                            )));
                          }}
                        />
                      )
                    ) : (
                      <div className="api-composer-query-row-spacer" />
                    )}

                    {hasNextRow ? (
                      <RichSelect
                        options={joinOptions}
                        value={cleanString(filterRow?.joinToNext).toLowerCase() === 'or' ? 'or' : 'and'}
                        placeholder="Join"
                        onChange={(event) => {
                          const nextJoin = cleanString(event.target.value).toLowerCase() === 'or' ? 'or' : 'and';
                          if (isPrimaryFilter) {
                            onComposerPredicateJoinChange(nextJoin);
                            return;
                          }
                          if (extraFilterIndex < 0) return;
                          onComposerExtraQueryFiltersChange((prev) => prev.map((row, rowIndex) => (
                            rowIndex === extraFilterIndex ? { ...row, joinToNext: nextJoin } : row
                          )));
                        }}
                      />
                    ) : (
                      <div className="api-composer-query-row-spacer" />
                    )}

                    {canRemove && (
                      <div
                        className="api-composer-query-remove"
                        style={{
                          cursor: canRemove ? 'pointer' : 'not-allowed',
                        }}
                        onClick={() => handleRemoveComposerFilter(filterRow)}
                        disabled={!canRemove}
                        type="button"
                      >
                        <Icon
                          iconKey="delete"
                          size={12}
                          color={theme.foregroundMuted}
                        />
                      </div>
                    )}

                  </div>
                );
              })}
            </div>

            <div className="api-composer-field api-composer-field-span-2 api-composer-advanced-section">
              <div className="api-composer-advanced-header">
                <label
                  className="api-composer-label"
                  style={{
                    color: theme.foregroundMuted,
                  }}
                >
                  Sort
                </label>
                <Button
                  label="+ Add Sort"
                  fontSize="11px"
                  height="28px"
                  padding="0 10px"
                  foregroundColor={theme.foreground}
                  style={{
                    borderColor: theme.status,
                    color: theme.foreground,
                  }}
                  disabled={selectedFields.length === 0}
                  onClick={() => {
                    if (selectedFields.length === 0) return;
                    onComposerQuerySortsChange((prev) => ([
                      ...prev,
                      {
                        fieldId: selectedFields[0]?.fieldId || '',
                        direction: 'asc',
                      },
                    ]));
                  }}
                />
              </div>

              {composerQuerySorts.length === 0 && (
                <div className="api-composer-empty" style={{ color: theme.foregroundMuted }}>
                  No sort rows added.
                </div>
              )}

              {composerQuerySorts.map((sortRow, index) => (
                <div
                  key={`composer-sort-row-${index}`}
                  className="api-composer-query-sort-row"
                >
                  <RichSelect
                    className="api-composer-rich-input"
                    style={{
                      backgroundColor: theme.input,
                      borderColor: theme.backgroundHover,
                      color: theme.foreground,
                    }}
                    options={composerFieldOptions}
                    emptyText="No fields loaded"
                    placeholder={selectedFields.length > 0 ? 'Select a field' : 'No fields loaded'}
                    disabled={selectedFields.length === 0}
                    value={cleanString(sortRow?.fieldId)}
                    onChange={(event) => {
                      const nextFieldId = cleanString(event.target.value);
                      onComposerQuerySortsChange((prev) => prev.map((row, rowIndex) => (
                        rowIndex === index ? { ...row, fieldId: nextFieldId } : row
                      )));
                    }}
                  />

                  <RichSelect
                    options={sortDirectionOptions}
                    value={cleanString(sortRow?.direction).toLowerCase() === 'desc' ? 'desc' : 'asc'}
                    placeholder="Direction"
                    onChange={(event) => {
                      const nextDirection = cleanString(event.target.value).toLowerCase() === 'desc' ? 'desc' : 'asc';
                      onComposerQuerySortsChange((prev) => prev.map((row, rowIndex) => (
                        rowIndex === index ? { ...row, direction: nextDirection } : row
                      )));
                    }}
                  />

                  <button
                    className="api-composer-query-remove"
                    style={{
                      borderColor: theme.backgroundHover,
                      color: theme.foregroundMuted,
                    }}
                    onClick={() => onComposerQuerySortsChange((prev) => prev.filter((_, rowIndex) => rowIndex !== index))}
                    type="button"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {showWriteFields && (
          <>
            <div className="api-composer-field api-composer-field-span-2">
              <label
                className="api-composer-label"
                style={{
                  color: theme.foregroundMuted,
                }}
                htmlFor="composer-write-title"
              >
                Title (optional)
              </label>
              <TextInput
                className="api-composer-input"
                style={{
                  backgroundColor: theme.input,
                  borderColor: theme.backgroundHover,
                  color: theme.foreground,
                }}
                id="composer-write-title"
                value={composerWriteTitle}
                placeholder="Task title"
                onChange={(event) => onComposerWriteTitleChange(event.target.value)}
              />
            </div>

            <div className="api-composer-field api-composer-field-span-2">
              <label
                className="api-composer-label"
                style={{
                  color: theme.foregroundMuted,
                }}
                htmlFor="composer-write-values"
              >
                values JSON
              </label>
              <TextInput
                multiline
                className="api-composer-textarea"
                style={{
                  backgroundColor: theme.input,
                  borderColor: theme.backgroundHover,
                  color: theme.foreground,
                }}
                id="composer-write-values"
                value={composerWriteValues}
                spellCheck={false}
                onChange={(event) => onComposerWriteValuesChange(event.target.value)}
              />
            </div>
          </>
        )}

      </div>

      {/* RUN TEST BUTTON */}
      <div className="api-composer-run-test-button">
        <Button
          label={composerBusy ? 'Running...' : 'Run Test'}
          iconKey="play_arrow"
          enabled={!composerBusy && Boolean(composerRequest.canTestRun)}
          onClick={onComposerTestRun}
        />
      </div>

      {/* TEST RESULT */}
      {composerResult && (
        <>
          <div className="api-composer-section-title"
            style={{
              marginTop: '16px',
            }}>Test Result</div>
          <pre
            className="api-composer-code"
            style={{
              backgroundColor: theme.input,
              borderColor: theme.backgroundHover,
            }}
          >
            {JSON.stringify(
              {
                body: composerResult.body,
                headers: composerResult.headers,
                status: composerResult.status,
              },
              null,
              2,
            )}
          </pre>
        </>
      )}

      {/* REQUEST ERROR */}
      {composerRequest?.error && (
        <div
          className="api-message api-message-error"
          style={{
            borderColor: theme.warning,
            color: theme.warning,
          }}
        >
          {composerRequest.error}
        </div>
      )}

      {/* TEST ERROR */}
      {composerError && (
        <div
          className="api-message api-message-error"
          style={{
            borderColor: theme.warning,
            color: theme.warning,
          }}
        >
          {composerError}
        </div>
      )}

      <Divider
        margin={24}
      />

      {/* REQUEST PREVIEW */}
      {!composerRequest?.error && composerPreview && (
        <>

          {/* cURL */}
          <div className="api-composer-section-title">cURL</div>
          <pre
            className="api-composer-code"
            style={{
              backgroundColor: theme.input,
              borderColor: theme.backgroundHover,
            }}
          >
            {composerCurlSnippet}
          </pre>

          <div className="api-composer-actions">
            <Button
              label="Copy cURL"
              iconKey="content_copy"
              height="30px"
              fontSize="12px"
              borderRadius="6px"
              onClick={() => onCopy(composerCurlSnippet, 'Composer cURL copied.')}
            />
          </div>

          <Divider
            margin={24}
          />

          {/* JAVASCRIPT */}
          <div className="api-composer-section-title">JavaScript</div>
          <pre
            className="api-composer-code"
            style={{
              backgroundColor: theme.input,
              borderColor: theme.backgroundHover,
            }}
          >
            {composerFetchSnippet}
          </pre>

          <div className="api-composer-actions">
            <Button
              label="Copy JavaScript"
              iconKey="content_copy"
              height="30px"
              fontSize="12px"
              borderRadius="6px"
              onClick={() => onCopy(composerFetchSnippet, 'Composer fetch snippet copied.')}
            />
          </div>
        </>
      )}

    </section>
  );
};

export default ApiComposer;
